import { ComboBox, IDropdownOption, Spinner, Stack } from '@fluentui/react';
import { ColumnDto, DataverseConfiguration, DataverseIntegration } from '../../../../../../../api/generated/data-contracts';
import { Dispatch, SetStateAction, useRef } from 'react';
import { DATAVERSE_CALENDAR_COLUMNS, DESELECT_KEY } from '../../../../../../../constants/queryKeys';
import produce from 'immer';
import { useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../../../api/api';
import { onRenderOption } from '../../../components/ComboBoxTooltip';
import { GetHostName } from '../../../../../../../helpers/trimUrl';

interface ICalendarPaneProps {
    config: DataverseConfiguration;
    dataverseTables: IDropdownOption<ColumnDto>[];
    setIntegration: Dispatch<SetStateAction<DataverseIntegration>>;
}

export const CalendarPane = (props: ICalendarPaneProps) => {
    const { dataverseUrl, calendarTableLogicalName } = props.config;

    const defaultColumns = useRef([] as IDropdownOption<ColumnDto>[]).current;
    const { data: calendarEntityOptions = defaultColumns } = useQuery({
        queryKey: [DATAVERSE_CALENDAR_COLUMNS, dataverseUrl, calendarTableLogicalName],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsTablesColumnsList({
                environmentUrl: GetHostName(dataverseUrl),
                tableName: calendarTableLogicalName,
            }).then(
                res =>
                    [
                        { key: DESELECT_KEY, text: 'Not selected', data: { logicalName: '' } },
                        ...res.data.map(
                            column => ({ key: column.logicalName, text: column.displayName || column.logicalName, data: column } as IDropdownOption<ColumnDto>),
                        ),
                    ] as IDropdownOption<ColumnDto>[],
            ),
        enabled: Boolean(dataverseUrl && calendarTableLogicalName),
        suspense: true,
    });

    return (
        <Stack tokens={{ childrenGap: 5 }}>
            <ComboBox
                label="Calendar Table"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={calendarTableLogicalName ? '' : 'Table required'}
                options={props.dataverseTables}
                onChange={(e, opt, index, value) => {
                    if (opt) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.calendarTableLogicalName = opt.key as string;
                                integration.configuration.calendarTableLogicalCollectionName = opt.data.logicalCollectionName;
                            }),
                        );
                    } else if (!value) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.calendarTableLogicalName = '';
                                integration.configuration.calendarTableLogicalCollectionName = '';
                            }),
                        );
                    }
                }}
                selectedKey={calendarTableLogicalName ?? ''}
                onRenderOption={onRenderOption}
            />
            {calendarTableLogicalName && !calendarEntityOptions && <Spinner />}
            <ComboBox
                label="Calendar Id"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={props.config.calendarIdColumn ? '' : 'Calendar Id column required'}
                options={calendarEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.calendarIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={calendarEntityOptions.length <= 0}
                selectedKey={props.config.calendarIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Calendar Name"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={props.config.calendarNameColumn ? '' : 'Calendar Name column required'}
                options={calendarEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.calendarNameColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={calendarEntityOptions.length <= 0}
                selectedKey={props.config.calendarNameColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Daily Capacity"
                allowFreeform
                autoComplete="on"
                options={calendarEntityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.calendarDailyCapacityColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={calendarEntityOptions.length <= 0}
                selectedKey={props.config.calendarDailyCapacityColumn ?? ''}
                onRenderOption={onRenderOption}
            />
        </Stack>
    );
};
